import { htmlToDOM, type DOMNode, type Element } from "html-react-parser-v6";

/**
 * Legal markup in, a contents list and a node tree out, in one parse. The tree
 * comes back with heading ids stamped in place, so entries and sections share
 * ids without a second walk.
 *
 * ⚠ Nothing here touches a browser global, so it runs in either environment —
 * the organism parses in the browser after its fetch, the specs parse in node.
 *
 * ⚠ `htmlToDOM` is used rather than `parse` because the caller needs the NODES:
 * it maps them onto DDS atoms, which a finished React tree no longer allows.
 */

/** Which headings become entries. Deeper than h4 is body structure, not an index. */
const HEADING_LEVELS: Record<string, number> = {
  h1: 1,
  h2: 2,
  h3: 3,
  h4: 4
};

/**
 * ⚠ Own-property lookup, not a bare index. `<constructor>` is the one tag name
 * that survives the parser's lower-casing AND names something on
 * `Object.prototype`, where a bare index would answer with a truthy function.
 *
 * DEFENSIVE, not a fix for an observed defect: measured, the node reaching here
 * for that tag carries `name: undefined`, so the lookup misses either way. No
 * test can make this guard bite — see the spec's note before writing one.
 */
function headingLevel(name: string): number | undefined {
  return Object.prototype.hasOwnProperty.call(HEADING_LEVELS, name) ?
    HEADING_LEVELS[name] :
    undefined;
}

export interface TocEntry {
  /** Shared with the heading's DOM id, so `#id` resolves. */
  id: string;
  label: string;
  /** 1–4, from the heading tag. Kept so a consumer can style by real depth. */
  level: number;
  children: TocEntry[];
}

export interface LegalDocument {
  /** From a `<title>` in the markup, or "" when the author wrote none. */
  title: string;
  toc: TocEntry[];
  /** The parsed tree, heading ids stamped, any stray `<title>` removed. */
  nodes: DOMNode[];
}

function emptyDocument(): LegalDocument {
  return {
    title: "",
    toc: [],
    nodes: []
  };
}

/**
 * ⚠ Structural, not `instanceof Element`: `html-dom-parser` ships separate
 * server and browser builds, each carrying its own `domhandler`, so
 * `instanceof` is false against the `Element` this package re-exports. Reading
 * `type` is what makes the module work in both.
 */
function isElement(node: DOMNode): node is Element {
  const { type } = (node as { type?: string });
  return type === "tag" || type === "script" || type === "style";
}

/** A node's visible text, joined across children. */
function textOf(node: DOMNode): string {
  if (!isElement(node)) {
    return "data" in node && typeof node.data === "string" ? node.data : "";
  }
  return (node.children as DOMNode[]).map(textOf).join("");
}

/**
 * ⚠ Walks `parent` by hand: domhandler nodes are not DOM nodes, so `closest`
 * does not exist — an optional call would read as working while never firing.
 */
function insideSvg(node: Element): boolean {
  for (let { parent } = node; parent; parent = parent.parent) {
    const candidate = parent as unknown as DOMNode;
    if (isElement(candidate) && candidate.name === "svg") {
      return true;
    }
  }
  return false;
}

/**
 * Lower-cased, hyphenated, and ASCII — diacritics are folded rather than
 * dropped, so "MiEspañol" reads `miespanol` instead of `mi-espa-ol`.
 *
 * ⚠ ASCII IS LOAD-BEARING, not a simplification. The Link atom's href
 * allowlist matches with ASCII `\w`, so `#miespañol` is refused and collapses
 * to `#` — a silently dead index entry. A label with no ASCII form at all
 * slugs to nothing and takes a positional id from `unique`.
 */
function slug(label: string): string {
  return label
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * First unused spelling of `base`, recording the SUFFIXED form it returns —
 * record only the base and "Terms", "Terms 2", "Terms" hands out `terms-2`
 * twice. A label that slugs to nothing gets a positional name, not an empty id.
 */
function unique(base: string, taken: Set<string>): string {
  let candidate = base === "" ? "section" : base;
  let n = 1;

  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base === "" ? "section" : base}-${n}`;
  }

  taken.add(candidate);
  return candidate;
}

/** The `id` an author wrote on a heading, trimmed, or "" when there is none. */
function authoredId(node: Element): string {
  const value = node.attribs?.id;
  return typeof value === "string" ? value.trim() : "";
}

/** Depth-first, so a heading inside a wrapper div is still found. */
function walk(nodes: DOMNode[], visit: (node: Element) => void): void {
  nodes.forEach(node => {
    if (!isElement(node)) {
      return;
    }
    visit(node);
    walk(node.children as DOMNode[], visit);
  });
}

/**
 * The author's `<title>`, with every stray one removed so none renders as loose
 * text. An SVG `<title>` is an icon's accessible name and is left alone.
 *
 * ⚠ Removal is deferred to after the walk, because splicing a children array
 * mid-iteration skips a sibling; and a root-level node has no `parent` to
 * splice from, so the top-level array is returned filtered instead.
 */
function takeTitle(nodes: DOMNode[]): { title: string; nodes: DOMNode[] } {
  const doomed = new Set<DOMNode>();
  let found = "";

  walk(nodes, node => {
    if (node.name !== "title" || insideSvg(node)) {
      return;
    }
    if (!found) {
      found = textOf(node).trim();
    }
    doomed.add(node);
  });

  doomed.forEach(node => {
    const siblings = (node as Element).parent?.children as DOMNode[] | undefined;
    const at = siblings?.indexOf(node) ?? -1;
    if (siblings && at >= 0) {
      siblings.splice(at, 1);
    }
  });

  return {
    title: found,
    nodes: nodes.filter(node => !doomed.has(node))
  };
}

/**
 * Stack-based nesting: a heading nests under the nearest PRECEDING heading of a
 * shallower level.
 *
 * ⚠ The test is the stack top's LEVEL, not the stack's depth — real legal
 * markup opens on a bare h2 and jumps h1 → h3.
 */
function collect(nodes: DOMNode[]): TocEntry[] {
  const entries: TocEntry[] = [];
  const stack: TocEntry[] = [];
  /**
   * ⚠ Every authored id is reserved BEFORE one is generated. In a single pass a
   * generated `offers` from an earlier heading claims the name first, and a
   * later `id="offers"` — a live deep link — is rewritten instead of kept.
   * Seeding `taken` with them also stops a generated slug taking a name an
   * authored heading further down still needs.
   */
  const reserved = new Set<string>();
  walk(nodes, node => {
    const authored = authoredId(node);
    if (headingLevel(node.name) && authored !== "") {
      reserved.add(authored);
    }
  });

  const taken = new Set<string>(reserved);

  walk(nodes, node => {
    const level = headingLevel(node.name);
    if (!level) {
      return;
    }

    /* A trailing colon punctuates the heading's place in the prose; it is not
       part of the section's name. */
    const label = textOf(node).trim().replace(/:$/, "").trim();
    if (!label) {
      return;
    }

    /* An author-written id is kept, so an existing deep link keeps working.
       `reserved` holds it from before any id was generated, so reaching it here
       means it is this heading's to claim; a second heading authoring the same
       id finds it consumed and takes a generated one. */
    const authored = authoredId(node);
    let id: string;
    if (authored !== "" && reserved.has(authored)) {
      reserved.delete(authored);
      id = authored;
    } else {
      id = unique(slug(label), taken);
    }
    taken.add(id);

    node.attribs = {
      ...node.attribs,
      id
    };

    const entry: TocEntry = {
      id,
      label,
      level,
      children: []
    };

    while (stack.length > 0 && stack[stack.length - 1].level >= level) {
      stack.pop();
    }
    (stack.length === 0 ? entries : stack[stack.length - 1].children).push(entry);
    stack.push(entry);
  });

  return entries;
}

/**
 * ⚠ Takes `unknown` and never throws: the input is a CMS response, so a null,
 * a number or an object can arrive where the contract promises a string, and a
 * Server Component that throws takes the whole page with it.
 */
export function parseLegalDocument(html: unknown): LegalDocument {
  if (typeof html !== "string" || html.trim() === "") {
    return emptyDocument();
  }

  const { title, nodes } = takeTitle(htmlToDOM(html) as DOMNode[]);

  return {
    title,
    toc: collect(nodes),
    nodes
  };
}
